import React from 'react';
import Immutable from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { Options } from 'highcharts';

import { StateType } from '../../reducers';
import { ImmutableInterface } from '../../types';
import { useAlphaChartRecordSet } from '../../shared/api/apiHooks';
import { FetchAlphaChartRecordSetResponse } from '../../shared/api/api';
import { chartMapping } from '../../shared/data/chartSeriesChange';
import { setAlphaTestPeriodShown } from '../../actions/AlphasActions';
import { isTestPeriodShownSelector } from '../../reducers/EditorReducer/EditorSelector';

export interface WithAlphaChartDataProps {
  path: string[];
  isSimulation?: boolean;
}

export interface ChartData {
  schema: FetchAlphaChartRecordSetResponse['schema'];
  records: Options['series'];
}

export interface AlphaChartDataState {
  isFetchingChartData: boolean;
  chartData: ImmutableInterface<ChartData> | null;
}

export function withAlphaChartData<T extends WithAlphaChartDataProps = WithAlphaChartDataProps>(
  Component: React.ComponentType<T & AlphaChartDataState>
) {
  const componentDisplayName = (Component?.displayName ?? Component?.name) || 'Component';

  function ComponentWithAlphaChartData(props: T) {
    const chartType = useSelector((state: StateType) => state.getIn(props.path.concat(['chartType']))) as string;

    const alphaDetailPath = props.path.concat(['alphaDetail']);
    const alphaDetail = useSelector((state: StateType) => state.getIn(alphaDetailPath)) as ImmutableInterface<any>;
    const isTestPeriodShown = useSelector((state: StateType) => isTestPeriodShownSelector(state, props));

    const alphaId = alphaDetail.get('id') as string;

    const isSimulation = !!props.isSimulation;

    const chartQuery = useAlphaChartRecordSet({ alphaId, chartType, isSimulation });

    const validationStartDate = alphaDetail.getIn(['validation', 'startDate']);
    const testStartDate = alphaDetail.getIn(['test', 'startDate']);
    const trainStartDate = alphaDetail.getIn(['validation', 'startDate']);
    const osStartDate = alphaDetail.getIn(['os', 'startDate']);
    const dispatch = useDispatch();

    const chartData = React.useMemo(() => {
      if (!chartQuery.data) {
        return null;
      }

      const getRecordSeries = chartMapping[chartType];

      return Immutable.fromJS({
        schema: chartQuery.data.schema,
        records: getRecordSeries({
          recordSet: chartType,
          chartDataSchema: chartQuery.data.schema,
          chartDataRecords: chartQuery.data.records,
          validationStartDate,
          trainStartDate,
          testStartDate,
          osStartDate,
          isSuperAlpha: alphaDetail.getIn(['type']) === 'SUPER',
          isTestPeriodShown,
        }),
      }) as ImmutableInterface<ChartData>;
    }, [chartQuery.data, alphaDetail, chartType, isTestPeriodShown]);

    const isTestPeriodControlShown = !!testStartDate && alphaDetail?.get('status') === 'UNSUBMITTED';

    const handleChangeTestPeriodShown = (isTestPeriodShown: boolean) =>
      dispatch(setAlphaTestPeriodShown(alphaDetailPath, isTestPeriodShown));

    return (
      <Component
        {...props}
        isFetchingChartData={chartQuery.isLoading}
        chartData={chartData}
        isTestPeriodControlShown={isTestPeriodControlShown}
        isTestPeriodShown={isTestPeriodShown}
        onChangeTestPeriodShown={handleChangeTestPeriodShown}
      />
    );
  }

  ComponentWithAlphaChartData.displayName = `withAlphaChartData(${componentDisplayName})`;

  return ComponentWithAlphaChartData;
}
